import type { AppType } from '@osteopro/api';
import { hc } from 'hono/client';

/**
 * LE client HTTP du front. Typé à partir des routes du serveur : une route renommée casse la
 * compilation ici. Aucun `fetch` brut ailleurs dans l'application.
 *
 * - `credentials: 'include'` parce que le jeton de rafraîchissement voyage en cookie httpOnly ;
 * - `X-Requested-With` est exigé par `/api/auth/refresh` (protection CSRF) ;
 * - `Authorization` est relu à chaque appel : le jeton d'accès vit en mémoire et tourne.
 */

let readAccessToken: () => string | null = () => null;

/** Branché une seule fois par le magasin d'authentification, qui détient le jeton en mémoire. */
export function setAccessTokenReader(reader: () => string | null): void {
  readAccessToken = reader;
}

export const api = hc<AppType>('/', {
  init: { credentials: 'include' },
  headers: () => {
    const token = readAccessToken();
    const headers: Record<string, string> = { 'X-Requested-With': 'osteopro' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  },
});

/** Erreur réseau normalisée. Le `code` est stable côté serveur : c'est lui qu'on traduit. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Forme minimale d'une réponse du client RPC. Suffit à `unwrap`, et reste typée par route. */
export interface UnwrappableResponse {
  readonly ok: boolean;
  readonly status: number;
  json: () => Promise<unknown>;
  text: () => Promise<string>;
}

/**
 * Charge utile d'une réponse du client RPC, déduite de la route appelée. Hono type les réponses
 * sans corps (204, 202) en `Promise<unknown>` : on les rend `undefined`, ce qui évite d'avoir
 * une deuxième fonction de lecture.
 */
export type Payload<R> = R extends { json: () => Promise<infer T> }
  ? unknown extends T
    ? undefined
    : T
  : never;

interface ServerErrorBody {
  error?: { code?: unknown; message?: unknown; details?: unknown };
}

function parseErrorBody(raw: string): { code: string; message: string; details?: unknown } {
  try {
    const parsed = JSON.parse(raw) as ServerErrorBody;
    const code = typeof parsed.error?.code === 'string' ? parsed.error.code : 'internal_error';
    const message = typeof parsed.error?.message === 'string' ? parsed.error.message : code;
    return { code, message, details: parsed.error?.details };
  } catch {
    return { code: 'internal_error', message: raw.slice(0, 200) };
  }
}

/**
 * LA façon de lire une réponse : le corps typé si tout va bien, une `ApiError` sinon.
 * C'est la seule fonction du front qui interprète une réponse HTTP.
 */
export async function unwrap<R extends UnwrappableResponse>(response: R): Promise<Payload<R>> {
  if (response.ok) {
    const body: unknown =
      response.status === 204 || response.status === 202 ? undefined : await response.json();
    return body as Payload<R>;
  }
  const { code, message, details } = parseErrorBody(await response.text());
  throw new ApiError(response.status, code, message, details);
}

/** Détails d'un 429 : le serveur indique combien de temps attendre. */
export function retryAfterSeconds(error: unknown): number | null {
  if (!isApiError(error) || error.status !== 429) return null;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return null;
  const value = (details as { retryAfterSeconds?: unknown }).retryAfterSeconds;
  return typeof value === 'number' ? value : null;
}

/**
 * Corps binaire d'une réponse : export RGPD, contenu d'un document. Vit ici, à côté d'`unwrap`,
 * pour que l'interprétation d'une réponse HTTP reste écrite une seule fois — même lecture du
 * code d'erreur, même `ApiError`, seule la charge utile diffère.
 */
export async function unwrapBlob(response: UnwrappableResponse & { blob: () => Promise<Blob> }) {
  if (response.ok) return response.blob();
  const { code, message, details } = parseErrorBody(await response.text());
  throw new ApiError(response.status, code, message, details);
}

/**
 * Déclenche l'enregistrement d'un contenu sous un nom de fichier. L'URL d'objet est révoquée dès
 * le clic consommé : elle retient le blob en mémoire tant qu'elle vit.
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = globalThis.document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  globalThis.document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
